import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, User } from '@/lib/api';
import { UserRole } from '@/types/user';

interface AuthContextType {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (fullName: string, email: string, password: string) => Promise<void>;
    logout: () => void;
    // Role-based helpers
    isAdmin: boolean;
    isManagement: boolean;
    hasRole: (...roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Check for existing auth on mount
    useEffect(() => {
        const storedToken = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
            setToken(storedToken);
            try {
                setUser(JSON.parse(storedUser));
            } catch {
                // Invalid stored user, clear storage
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');
            }
        }
        setIsLoading(false);
    }, []);

    const login = async (email: string, password: string) => {
        const response = await authApi.login({ email, password });

        if (response.data.success) {
            const { user: userData, token: authToken } = response.data.data;

            setUser(userData);
            setToken(authToken);

            localStorage.setItem('auth_token', authToken);
            localStorage.setItem('user', JSON.stringify(userData));
        } else {
            throw new Error(response.data.error?.message || 'خطأ في تسجيل الدخول');
        }
    };

    const register = async (fullName: string, email: string, password: string) => {
        const response = await authApi.register({ fullName, email, password });

        if (response.data.success) {
            const { user: userData, token: authToken } = response.data.data;

            setUser(userData);
            setToken(authToken);

            localStorage.setItem('auth_token', authToken);
            localStorage.setItem('user', JSON.stringify(userData));
        } else {
            throw new Error(response.data.error?.message || 'خطأ في إنشاء الحساب');
        }
    };

    const logout = () => {
        // Call logout API (optional, fire and forget)
        authApi.logout().catch(() => { });

        setUser(null);
        setToken(null);

        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                token,
                isAuthenticated: !!token,
                isLoading,
                login,
                register,
                logout,
                // Role-based helpers - now support multiple roles
                isAdmin: user?.roles?.includes('admin') ?? false,
                isManagement: user?.roles?.some(r => ['admin', 'partner'].includes(r)) ?? false,
                hasRole: (...roles) => user?.roles?.some(r => roles.includes(r)) ?? false,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
